import {
  CircleCheck, Clock, Crown, Info, Target, Trophy,
} from "lucide-react";
import type { ScoringRule, ScoringRules } from "@/features/predictions/types/competition.type";
import { deriveScoringRules } from "@/features/predictions/utils/deriveScoringRules";
import { ResponsiveDialog } from "@/shared/components/responsiveDialog";

type Props = {
  open: boolean
  onClose: () => void
  scoringRules: ScoringRules | null
};

type RulePresentation = {
  icon: typeof Trophy
  variantClass: string
};

// Presentation is frontend vocabulary mapped from the API's semantic rule
// keys (same pattern as MatchCard's VARIANT_CLASS). Unknown keys render with
// a neutral icon and badge — never dropped, never crashing.
const RULE_PRESENTATION_BY_KEY: Record<string, RulePresentation> = {
  exact_score: {
    icon: Trophy,
    variantClass: "bg-amber-50 text-amber-800 ring-amber-200 dark:bg-amber-950/60 dark:text-amber-200 dark:ring-amber-900",
  },
  result_and_diff: {
    icon: Target,
    variantClass: "bg-emerald-50 text-emerald-800 ring-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-200 dark:ring-emerald-900",
  },
  result_only: {
    icon: Crown,
    variantClass: "bg-sky-50 text-sky-800 ring-sky-200 dark:bg-sky-950/60 dark:text-sky-200 dark:ring-sky-900",
  },
  correct_qualifier: {
    icon: CircleCheck,
    variantClass: "bg-violet-50 text-violet-800 ring-violet-200 dark:bg-violet-950/60 dark:text-violet-200 dark:ring-violet-900",
  },
  tiebreaker: {
    icon: Clock,
    variantClass: "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700",
  },
};

const NEUTRAL_PRESENTATION: RulePresentation = {
  icon: Info,
  variantClass: "bg-gray-100 text-gray-600 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700",
};

const resolvePresentation = (ruleKey: string): RulePresentation =>
  RULE_PRESENTATION_BY_KEY[ruleKey] ?? NEUTRAL_PRESENTATION;

const formatPoints = (points: number) => `+${points} pt${points > 1 ? "s" : ""}`;

const RuleCard = ({ rule }: { rule: ScoringRule }) => {
  const presentation = resolvePresentation(rule.key);
  const Icon = presentation.icon;
  return (
    <li className="flex items-start gap-3 rounded-2xl border border-gray-100 bg-gray-50 p-4 dark:border-gray-800 dark:bg-gray-900/40">
      <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-inset ring-gray-200 dark:bg-gray-800 dark:ring-gray-700">
        <Icon size={18} className="text-gray-700 dark:text-gray-200" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            {rule.label}
          </h3>
          {rule.points !== undefined && (
            <span
              className={`inline-flex shrink-0 items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${presentation.variantClass}`}
            >
              {formatPoints(rule.points)}
            </span>
          )}
        </div>
        {rule.description && (
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {rule.description}
          </p>
        )}
      </div>
    </li>
  );
};

export const RulesDialog = ({ open, onClose, scoringRules }: Props) => {
  const { sections, examples, footnotes } = deriveScoringRules(scoringRules);

  return (
    <ResponsiveDialog
      open={open}
      onClose={onClose}
      size="2xl"
      title="Règles du jeu"
      description="Comment les points sont attribués à chaque match."
    >
      <div className="space-y-5">
        {sections.map((section) => (
          <section key={section.key} className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {section.title}
            </p>
            <ul className="space-y-3">
              {section.rules.map((rule) => (
                <RuleCard key={rule.key} rule={rule} />
              ))}
            </ul>
          </section>
        ))}

        {examples.length > 0 && (
          <section className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Exemples
            </p>
            <div className="overflow-x-auto rounded-2xl border border-gray-100 dark:border-gray-800">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 dark:bg-gray-900/60 dark:text-gray-400">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium">Match</th>
                    <th scope="col" className="whitespace-nowrap px-3 py-2 text-left font-medium">Pronostic</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium">Détail</th>
                    <th scope="col" className="whitespace-nowrap px-3 py-2 text-right font-medium">Points</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white dark:divide-gray-800 dark:bg-gray-900/30">
                  {examples.map((example) => (
                    <tr key={`${example.match}-${example.prediction}`}>
                      <td className="px-3 py-2 align-top text-gray-700 dark:text-gray-200">
                        {example.match}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-top text-gray-700 dark:text-gray-200">
                        {example.prediction}
                      </td>
                      <td className="px-3 py-2 align-top text-gray-500 dark:text-gray-400">
                        {example.detail}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-top text-right font-semibold text-gray-900 dark:text-white">
                        {example.points}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {footnotes.map((footnote, index) => (
          <p
            key={footnote}
            className={index === 0
              ? "text-center text-xs text-gray-500 dark:text-gray-400"
              : "text-center text-[11px] text-gray-400 dark:text-gray-500"}
          >
            {footnote}
          </p>
        ))}
      </div>
    </ResponsiveDialog>
  );
};

RulesDialog.displayName = "RulesDialog";
